import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { jwtDecode } from "jwt-decode";
import {
  Building2,
  FileText,
  ImagePlus,
  MoreHorizontal,
  Paperclip,
  Pencil,
  Reply,
  Send,
  Settings,
  SmilePlus,
  Trash2,
  Users,
  X,
  ChevronLeft,
} from "lucide-react";
import { useAuth } from "../../scripts/AuthContext";
import {
  deletePartnerMessage,
  getPartnerChannel,
  listPartnerMessages,
  markPartnerChannelRead,
  resolvePartnerPolicy,
  sendPartnerMedia,
  sendPartnerMessage,
  togglePartnerReaction,
  updatePartnerMessage,
} from "@/lib/partnerApi";
import { usePartnerSocket } from "../../scripts/PartnerSocketContext";
import { DM_QUICK_REACT_EMOJIS } from "@/lib/dmApi";
import { findSeenReceiptMessageId } from "@/lib/dmSeenReceipt";
import { markdownToPlain, plainExcerpt } from "@/lib/markdownPlain";
import { getFullName } from "@/lib/userUtils";
import { resolveAvatarUrl } from "@/lib/avatarApi";
import { toastError, toastSuccess } from "@/lib/toast";
import {
  MEDIA_IMAGE_MAX_BYTES,
  MEDIA_MESSAGE_MAX_FILES,
  MEDIA_VIDEO_MAX_BYTES,
  isImageFile,
  isVideoFile,
} from "@/lib/parseVideoEmbed";
import {
  TEAM_FILE_ACCEPT,
  isAllowedTeamFileUpload,
} from "@/lib/teamFileUpload";
import {
  partnerBlobAttachmentFromMedia,
  partnerGifItemsFromMedia,
} from "@/lib/partnerMediaAttachment";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import MemberProfileHover from "@/components/MemberProfileHover";
import MarkdownBody from "../MarkdownBody";
import EmojiInsertButton from "../EmojiInsertButton";
import DmFileAttachments from "./DmFileAttachments";
import DmGifAttachment from "./DmGifAttachment";
import DmMessageContextMenu, {
  DmMessageContextTarget,
} from "./DmMessageContextMenu";
import DmMessageReactions from "./DmMessageReactions";
import GifPickerButton from "./GifPickerButton";
import MobileMessageComposer from "./MobileMessageComposer";
import PartnerChannelMembersPanel, {
  partnerMembersDescription,
} from "./PartnerChannelMembersPanel";
import PartnerChannelSettingsForm, {
  formatRetentionDays,
} from "./PartnerChannelSettingsForm";
import RichMessageEditor, {
  isMarkdownEmpty,
  normalizeEditorMarkdown,
} from "./RichMessageEditor";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { pinChatScrollToBottom } from "@/lib/pinChatScrollToBottom";
import {
  formatDmTimestamp,
  getMessageClusterMeta,
} from "@/lib/dmMessageCluster";

function hasOsFiles(dt) {
  if (!dt) return false;
  if (dt.files && dt.files.length > 0) return true;
  return Array.from(dt.types || []).includes("Files");
}

function PendingLocalChip({ file, onRemove }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (!isImageFile(file)) return undefined;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return (
    <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5 text-xs max-w-full">
      {previewUrl ? (
        <img
          src={previewUrl}
          alt=""
          className="h-10 w-10 rounded object-cover shrink-0"
        />
      ) : isImageFile(file) ? (
        <ImagePlus className="h-3.5 w-3.5 shrink-0" />
      ) : (
        <FileText className="h-3.5 w-3.5 shrink-0" />
      )}
      <span className="truncate flex-1">{file.name}</span>
      <button
        type="button"
        className="p-0.5 hover:opacity-70"
        onClick={onRemove}
        aria-label={`Remove ${file.name}`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Partner senders/members carry display_name; fall back when names are absent. */
function personName(person) {
  if (!person) return "Unknown";
  if (person.first_name || person.last_name) return getFullName(person);
  return person.display_name || "Unknown";
}

/**
 * Profile preview for a message sender. People from the partner org get the
 * card without presence or the org-scoped full profile, both of which only
 * exist for your own org's members.
 */
function SenderProfileHover({ sender, sameOrg, side, className, children }) {
  return (
    <MemberProfileHover
      user={sender}
      side={side}
      className={className}
      showPresence={sameOrg}
      allowFullProfile={sameOrg}
    >
      {children}
    </MemberProfileHover>
  );
}

function PartnerMembersDialog({
  open,
  onOpenChange,
  channel,
  viewerOrgId,
  channelId,
  accessToken,
  onChannelChange,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Channel members</DialogTitle>
          <DialogDescription>
            {partnerMembersDescription(channel, viewerOrgId)}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <PartnerChannelMembersPanel
            channel={channel}
            viewerOrgId={viewerOrgId}
            channelId={channelId}
            accessToken={accessToken}
            onChannelChange={onChannelChange}
            active={open}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PartnerSettingsDialog({
  open,
  onOpenChange,
  channel,
  orgId,
  channelId,
  accessToken,
  onChannelChange,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Channel settings</DialogTitle>
          <DialogDescription>
            Either org can rename the channel. Retention changes and legal-hold
            releases need both organizations to agree.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <PartnerChannelSettingsForm
            channel={channel}
            orgId={orgId}
            channelId={channelId}
            accessToken={accessToken}
            onChannelChange={onChannelChange}
            showExport
            showEndPartnershipHint
            onEndPartnershipHintClick={() => onOpenChange(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PartnerThread({ orgId, channelId, onBack }) {
  const { accessToken } = useAuth();
  const isMobile = useIsMobile();
  const { subscribe, joinChannel, emitTyping } = usePartnerSocket();
  const [channel, setChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [membersOpen, setMembersOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pendingGif, setPendingGif] = useState(null);
  const [pendingLocalFiles, setPendingLocalFiles] = useState([]);
  const [dropActive, setDropActive] = useState(false);
  const [typingUserId, setTypingUserId] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [messageMenu, setMessageMenu] = useState(null);
  const [memberReadAt, setMemberReadAt] = useState([]);
  const scrollRef = useRef(null);
  const listContentRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const composerRef = useRef(null);
  const editComposerRef = useRef(null);
  const fileInputRef = useRef(null);
  const typingClearRef = useRef(null);
  const lastTypedEmitRef = useRef(0);
  const dropDepthRef = useRef(0);

  const pendingFileCount = pendingLocalFiles.length;
  const remainingAttach = Math.max(0, MEDIA_MESSAGE_MAX_FILES - pendingFileCount);

  const addLocalFiles = useCallback((incoming) => {
    const list = Array.from(incoming || []).filter(Boolean);
    if (!list.length) return;
    const accepted = [];
    for (const file of list) {
      if (!isAllowedTeamFileUpload(file.name, file.type)) {
        toastError(`File type not allowed: ${file.name}`);
        continue;
      }
      const tooBig = isVideoFile(file)
        ? file.size > MEDIA_VIDEO_MAX_BYTES
        : file.size > MEDIA_IMAGE_MAX_BYTES;
      if (tooBig) {
        toastError(
          `${file.name} exceeds the ${isVideoFile(file) ? "100MB" : "10MB"} limit`
        );
        continue;
      }
      accepted.push(file);
    }
    if (!accepted.length) return;
    setPendingGif(null);
    setPendingLocalFiles((prev) => {
      const room = MEDIA_MESSAGE_MAX_FILES - prev.length;
      if (room <= 0) {
        toastError(`You can attach up to ${MEDIA_MESSAGE_MAX_FILES} files`);
        return prev;
      }
      if (accepted.length > room) {
        toastError(`You can attach up to ${MEDIA_MESSAGE_MAX_FILES} files`);
      }
      return [...prev, ...accepted.slice(0, room)];
    });
  }, []);

  const currentUserId = useMemo(() => {
    if (!accessToken) return null;
    try {
      return jwtDecode(accessToken)?.sub || null;
    } catch {
      return null;
    }
  }, [accessToken]);

  const load = useCallback(async () => {
    if (!accessToken || !orgId || !channelId) return;
    setLoading(true);
    setError(null);
    try {
      const [ch, msgs] = await Promise.all([
        getPartnerChannel(accessToken, orgId, channelId),
        listPartnerMessages(accessToken, orgId, channelId, { limit: 80 }),
      ]);
      setChannel(ch);
      setMessages(msgs);
      setMemberReadAt(
        Array.isArray(ch?.member_read_at) ? ch.member_read_at : []
      );
      stickToBottomRef.current = true;
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load partner channel"
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken, orgId, channelId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setReplyTo(null);
    setEditingId(null);
    setEditDraft("");
    setMessageMenu(null);
    setMessages([]);
    setChannel(null);
    stickToBottomRef.current = true;
  }, [channelId]);

  const closeMessageMenu = useCallback(() => setMessageMenu(null), []);

  const applyChannelUpdate = useCallback((next) => {
    if (!next) return;
    setChannel((prev) => ({
      ...prev,
      ...next,
      members: next.members ?? prev?.members,
    }));
    if (Array.isArray(next.member_read_at)) {
      setMemberReadAt(next.member_read_at);
    }
  }, []);

  const seenReceiptMessageId = useMemo(() => {
    const normalized = (messages || []).map((m) => ({
      message_id: m.message_id,
      sender_id: m.sender?.user_id,
      created_at: m.created_at,
      deleted_at: m.deleted_at,
    }));
    return findSeenReceiptMessageId(
      normalized,
      currentUserId,
      null,
      memberReadAt
    );
  }, [messages, currentUserId, memberReadAt]);

  useEffect(() => {
    if (!channelId) return;
    joinChannel(channelId);
  }, [channelId, joinChannel]);

  useEffect(() => {
    if (!accessToken || !orgId || !channelId || loading) return;
    markPartnerChannelRead(accessToken, orgId, channelId).catch(() => {});
  }, [accessToken, orgId, channelId, loading, messages.length]);

  useEffect(() => {
    if (!subscribe || !channelId) return undefined;
    return subscribe((event, payload) => {
      if (Number(payload?.channel_id) !== Number(channelId)) return;

      if (event === "partner.channel.updated" && payload.channel) {
        applyChannelUpdate(payload.channel);
        return;
      }

      if (
        event === "partner.message" ||
        event === "partner.message.updated" ||
        event === "partner.message.deleted" ||
        event === "partner.message.reaction"
      ) {
        const msg = payload.message;
        if (!msg?.message_id) return;
        setMessages((prev) => {
          const idx = prev.findIndex(
            (m) => Number(m.message_id) === Number(msg.message_id)
          );
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = { ...next[idx], ...msg };
            return next;
          }
          if (event === "partner.message") {
            return [...prev, msg];
          }
          return prev;
        });
        if (
          event === "partner.message" &&
          msg.sender?.user_id !== currentUserId
        ) {
          setTypingUserId(null);
        }
        return;
      }

      if (event === "partner.typing") {
        if (payload.user_id === currentUserId) return;
        if (payload.typing) {
          setTypingUserId(payload.user_id);
          if (typingClearRef.current) clearTimeout(typingClearRef.current);
          typingClearRef.current = setTimeout(() => {
            setTypingUserId(null);
          }, 4000);
        } else {
          setTypingUserId(null);
        }
        return;
      }

      if (event === "partner.read") {
        if (
          payload?.user_id &&
          currentUserId &&
          String(payload.user_id) !== String(currentUserId)
        ) {
          setMemberReadAt((prev) => {
            const next = (prev || []).filter(
              (m) => String(m.user_id) !== String(payload.user_id)
            );
            next.push({
              user_id: payload.user_id,
              last_read_at: payload.last_read_at || null,
            });
            return next;
          });
        }
      }
    });
  }, [subscribe, channelId, applyChannelUpdate, currentUserId]);

  useEffect(() => {
    return () => {
      if (typingClearRef.current) clearTimeout(typingClearRef.current);
    };
  }, []);

  useEffect(() => {
    setPendingLocalFiles([]);
    setPendingGif(null);
    setReplyTo(null);
    dropDepthRef.current = 0;
    setDropActive(false);
  }, [channelId]);

  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = true;
    if (smooth) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    } else {
      pinChatScrollToBottom(el);
    }
  }, []);

  useLayoutEffect(() => {
    if (!stickToBottomRef.current) return;
    scrollToBottom(false);
  }, [messages, channelId, loading, scrollToBottom]);

  // Flex panel height often settles after the first pin; observe both the
  // scrollport and the message content so open always lands on the latest.
  useEffect(() => {
    const el = scrollRef.current;
    const content = listContentRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const pin = () => {
      if (!stickToBottomRef.current) return;
      el.scrollTop = el.scrollHeight;
    };
    const ro = new ResizeObserver(pin);
    ro.observe(el);
    if (content) ro.observe(content);
    pin();
    return () => ro.disconnect();
  }, [channelId, loading, messages.length]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollTop = el.scrollTop;
    const prevTop = lastScrollTopRef.current;
    lastScrollTopRef.current = scrollTop;
    if (
      isMobile &&
      prevTop - scrollTop > 12 &&
      document.activeElement?.closest?.("[contenteditable]")
    ) {
      composerRef.current?.blur?.();
    }
    const nearBottom =
      el.scrollHeight - scrollTop - el.clientHeight < 80;
    stickToBottomRef.current = nearBottom;
  };

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const content = normalizeEditorMarkdown(
      composerRef.current?.getMarkdown?.() ?? draft
    );
    const empty = isMarkdownEmpty(content);
    if (
      (empty && !pendingGif && pendingFileCount === 0) ||
      !accessToken ||
      sending ||
      channel?.can_write === false
    ) {
      return;
    }
    const outboundReply = replyTo;
    const outboundFiles = pendingLocalFiles;
    setReplyTo(null);
    setSending(true);
    try {
      const msg =
        outboundFiles.length > 0
          ? await sendPartnerMedia(accessToken, orgId, channelId, {
              files: outboundFiles,
              content: empty ? "" : content,
              reply_to_message_id: outboundReply?.message_id,
            })
          : await sendPartnerMessage(accessToken, orgId, channelId, {
              content: empty ? "" : content,
              media: pendingGif ? [pendingGif] : undefined,
              reply_to_message_id: outboundReply?.message_id,
            });
      setMessages((prev) => {
        if (prev.some((m) => Number(m.message_id) === Number(msg.message_id))) {
          return prev.map((m) =>
            Number(m.message_id) === Number(msg.message_id) ? msg : m
          );
        }
        return [...prev, msg];
      });
      setDraft("");
      composerRef.current?.clear?.();
      setPendingGif(null);
      setPendingLocalFiles([]);
    } catch (err) {
      if (outboundReply) setReplyTo(outboundReply);
      if (outboundFiles.length) setPendingLocalFiles(outboundFiles);
      setError(
        err?.response?.data?.message || err?.message || "Failed to send"
      );
    } finally {
      setSending(false);
    }
  };

  const upsertMessage = useCallback((msg) => {
    if (!msg?.message_id) return;
    setMessages((prev) => {
      const idx = prev.findIndex(
        (m) => Number(m.message_id) === Number(msg.message_id)
      );
      if (idx < 0) return [...prev, msg];
      const next = [...prev];
      next[idx] = { ...next[idx], ...msg };
      return next;
    });
  }, []);

  const startEdit = (msg) => {
    setEditingId(msg.message_id);
    setEditDraft(msg.content || "");
    setMessageMenu(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft("");
  };

  const saveEdit = async () => {
    const content = normalizeEditorMarkdown(
      editComposerRef.current?.getMarkdown?.() ?? editDraft
    );
    if (isMarkdownEmpty(content) || !editingId) return;
    try {
      const updated = await updatePartnerMessage(
        accessToken,
        orgId,
        channelId,
        editingId,
        content
      );
      upsertMessage(updated);
      cancelEdit();
    } catch (err) {
      toastError(err, "Could not edit message");
    }
  };

  const handleDelete = async (messageId) => {
    try {
      const updated = await deletePartnerMessage(
        accessToken,
        orgId,
        channelId,
        messageId
      );
      upsertMessage(updated);
    } catch (err) {
      toastError(err, "Could not delete message");
    }
  };

  const reactToMessage = async (messageId, emoji) => {
    try {
      const updated = await togglePartnerReaction(
        accessToken,
        orgId,
        channelId,
        messageId,
        emoji
      );
      upsertMessage(updated);
    } catch (err) {
      toastError(err, "Could not update reaction");
    }
  };

  const copyMessageText = async (msg) => {
    const text = markdownToPlain(msg?.content || "").trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toastSuccess("Copied");
    } catch {
      toastError("Could not copy");
    }
  };

  const onPickFiles = (e) => {
    const files = e.target.files;
    e.target.value = "";
    if (!files?.length) return;
    addLocalFiles(files);
  };

  const onComposerPaste = (e) => {
    const native = e.nativeEvent || e;
    const items = native?.clipboardData?.items;
    if (!items?.length) return;
    const files = [];
    for (const item of items) {
      if (item.kind !== "file") continue;
      const f = item.getAsFile();
      if (f) files.push(f);
    }
    if (!files.length) return;
    e.preventDefault?.();
    addLocalFiles(files);
  };

  const canDropFiles =
    !!channel && channel.can_write !== false && !editingId;

  const onDropZoneDragEnter = (e) => {
    if (!canDropFiles || !hasOsFiles(e.dataTransfer)) return;
    e.preventDefault();
    dropDepthRef.current += 1;
    setDropActive(true);
  };

  const onDropZoneDragLeave = (e) => {
    if (!hasOsFiles(e.dataTransfer)) return;
    e.preventDefault();
    dropDepthRef.current = Math.max(0, dropDepthRef.current - 1);
    if (dropDepthRef.current === 0) setDropActive(false);
  };

  const onDropZoneDragOver = (e) => {
    if (!canDropFiles || !hasOsFiles(e.dataTransfer)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const onDropZoneDrop = (e) => {
    e.preventDefault();
    dropDepthRef.current = 0;
    setDropActive(false);
    if (!canDropFiles) return;
    const files = e.dataTransfer?.files;
    if (files?.length) addLocalFiles(files);
  };

  if (error && !channel && !loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-destructive p-6">
        {error}
      </div>
    );
  }

  const readOnly = channel?.can_write === false;
  const legalHold = !!channel?.legal_hold;
  const partnerName = channel?.partner_org?.name || "Partner org";
  const partnerAvatar = resolveAvatarUrl(channel?.partner_org?.avatar_url);
  const memberCount = (channel?.members || []).length;
  const pendingPolicy = channel?.pending_policy || null;
  const pendingRelease = channel?.pending_legal_hold_release || null;
  const canResolveSettings = !!channel?.can_manage_settings;
  const retentionProposedByUs = pendingPolicy?.proposed_by_org_id === orgId;
  const releaseProposedByUs = pendingRelease?.proposed_by_org_id === orgId;

  const resolveProposal = async (proposalId, accept, successMessage) => {
    try {
      const res = await resolvePartnerPolicy(
        accessToken,
        orgId,
        channelId,
        proposalId,
        accept
      );
      if (res?.channel) applyChannelUpdate(res.channel);
      toastSuccess(successMessage);
    } catch (err) {
      toastError(err, "Could not resolve proposal");
    }
  };

  return (
    <div
      className="relative flex flex-col h-full min-h-0 min-w-0 overflow-x-hidden bg-background"
      onDragEnter={canDropFiles ? onDropZoneDragEnter : undefined}
      onDragLeave={canDropFiles ? onDropZoneDragLeave : undefined}
      onDragOver={canDropFiles ? onDropZoneDragOver : undefined}
      onDrop={canDropFiles ? onDropZoneDrop : undefined}
    >
      {dropActive && canDropFiles ? (
        <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center rounded-md border-2 border-dashed border-primary bg-primary/10">
          <p className="text-sm font-medium text-primary">Drop files to attach</p>
        </div>
      ) : null}
      <div
        className={cn(
          "flex items-center gap-3 border-b border-border px-3 shrink-0 min-w-0",
          isMobile ? "py-2" : "py-2.5"
        )}
      >
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="md:hidden shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
            aria-label="Back to conversations"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        ) : null}
        <Avatar className={cn("shrink-0", isMobile ? "h-8 w-8" : "h-10 w-10")}>
          {partnerAvatar ? (
            <AvatarImage src={partnerAvatar} alt={partnerName} />
          ) : null}
          <AvatarFallback className="text-sm">
            <Building2 className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <p className="font-semibold text-base truncate">
              {channel?.title || (loading ? "Loading…" : "Partner channel")}
            </p>
            {!isMobile ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-medium rounded-full border border-border text-muted-foreground px-2 py-0.5 shrink-0">
                <Building2 className="h-3 w-3" />
                Partner
              </span>
            ) : null}
            {legalHold ? (
              <span className="text-[10px] font-medium rounded-full border border-amber-500/50 text-amber-700 dark:text-amber-400 px-2 py-0.5 shrink-0">
                Legal hold
              </span>
            ) : null}
          </div>
          {!isMobile ? (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {loading && !channel
                ? "Loading partner channel…"
                : `with ${partnerName}${memberCount ? ` · ${memberCount} members` : ""} · Retention ${formatRetentionDays(channel?.retention_days)}`}
            </p>
          ) : null}
        </div>
        {channel ? (
          isMobile ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label="Channel actions"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-w-[16rem]">
                <DropdownMenuItem disabled className="text-xs text-muted-foreground focus:bg-transparent">
                  Partner · with {partnerName}
                  {memberCount ? ` · ${memberCount} members` : ""}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setMembersOpen(true)}>
                  <Users className="h-4 w-4 mr-2" />
                  {channel?.can_manage_members ? "Manage members" : "Members"}
                </DropdownMenuItem>
                {channel?.can_manage_settings ? (
                  <DropdownMenuItem onClick={() => setSettingsOpen(true)}>
                    <Settings className="h-4 w-4 mr-2" />
                    Channel settings
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="shrink-0"
                onClick={() => setMembersOpen(true)}
              >
                <Users className="h-4 w-4 mr-1" />
                {channel?.can_manage_members ? "Manage members" : "Members"}
              </Button>
              {channel?.can_manage_settings ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  title="Channel settings"
                  aria-label="Channel settings"
                  onClick={() => setSettingsOpen(true)}
                >
                  <Settings className="h-4 w-4" />
                </Button>
              ) : null}
            </>
          )
        ) : null}
      </div>

      {legalHold ? (
        <div className="shrink-0 border-b border-border bg-amber-500/15 px-3 py-2 text-xs text-amber-950 dark:text-amber-100 space-y-1.5">
          <p className="font-medium">
            Legal hold — messaging is frozen until both organizations release
            it.
          </p>
          {pendingRelease ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-amber-900/90 dark:text-amber-100/90">
                {releaseProposedByUs
                  ? `Release proposed — waiting on ${partnerName}.`
                  : `${partnerName} proposed releasing the hold.`}
              </p>
              {canResolveSettings && !releaseProposedByUs ? (
                <>
                  <Button
                    type="button"
                    size="sm"
                    className="h-7"
                    onClick={() =>
                      resolveProposal(
                        pendingRelease.proposal_id,
                        true,
                        "Legal hold cleared"
                      )
                    }
                  >
                    Accept release
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7"
                    onClick={() =>
                      resolveProposal(
                        pendingRelease.proposal_id,
                        false,
                        "Release rejected — hold remains"
                      )
                    }
                  >
                    Reject
                  </Button>
                </>
              ) : null}
            </div>
          ) : canResolveSettings ? (
            <p className="text-amber-900/80 dark:text-amber-100/80">
              Open channel settings to propose a release.
            </p>
          ) : null}
        </div>
      ) : readOnly ? (
        <div className="shrink-0 border-b border-border bg-amber-500/10 px-3 py-2 text-xs text-amber-950 dark:text-amber-100">
          {channel?.write_block_reason || "This partner channel is read-only."}
        </div>
      ) : null}
      {pendingPolicy ? (
        <div className="shrink-0 border-b border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <p>
              Retention change pending (
              {formatRetentionDays(pendingPolicy.proposed_retention_days)})
              {retentionProposedByUs
                ? ` — waiting on ${partnerName}.`
                : ` — proposed by ${partnerName}.`}
            </p>
            {canResolveSettings && !retentionProposedByUs ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  className="h-7"
                  onClick={() =>
                    resolveProposal(
                      pendingPolicy.proposal_id,
                      true,
                      "Retention updated"
                    )
                  }
                >
                  Accept
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7"
                  onClick={() =>
                    resolveProposal(
                      pendingPolicy.proposal_id,
                      false,
                      "Proposal rejected"
                    )
                  }
                >
                  Reject
                </Button>
              </>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="relative flex-1 min-h-0 min-w-0 overflow-x-hidden">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="h-full min-w-0 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-hide px-3 py-2"
        >
          <div
            ref={listContentRef}
            className={cn(
              "min-w-0 w-full",
              isMobile ? "space-y-1" : "space-y-4"
            )}
          >
          {loading && messages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Loading messages…
            </p>
          ) : messages.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No messages yet. Say hello to {partnerName}.
            </p>
          ) : (
            messages.map((m, msgIndex) => {
              const cluster = getMessageClusterMeta(messages, msgIndex);
              const mine =
                m.sender?.user_id === currentUserId &&
                m.sender?.org_id === orgId;
              const sameOrg = m.sender?.org_id === orgId;
              const isEditing = editingId === m.message_id;
              const showIncomingAvatar =
                !mine && (!isMobile || cluster.startOfRun);
              const reserveAvatarSlot =
                !mine && isMobile && !cluster.startOfRun;
              const showSenderMeta =
                !isEditing && (!isMobile || cluster.startOfRun);
              const showTimestamp =
                !isEditing && (!isMobile || cluster.showTimestamp);

              if (m.deleted_at) {
                return (
                  <div
                    key={m.message_id}
                    className={cn(
                      "flex w-full min-w-0 items-start",
                      isMobile ? "gap-2" : "gap-3",
                      mine ? "justify-end" : "justify-start",
                      isMobile && cluster.startOfRun && "mt-2"
                    )}
                  >
                    {showIncomingAvatar ? (
                      <SenderProfileHover
                        sender={m.sender}
                        sameOrg={sameOrg}
                        side="right"
                      >
                        <OrgMemberAvatar
                          user={m.sender}
                          className={cn(
                            "shrink-0 mt-0.5",
                            isMobile ? "h-7 w-7" : "h-9 w-9"
                          )}
                          fallbackClassName="text-xs font-semibold"
                        />
                      </SenderProfileHover>
                    ) : reserveAvatarSlot ? (
                      <div className="w-7 shrink-0" aria-hidden />
                    ) : null}
                    <div
                      className={cn(
                        "max-w-[min(85%,100%)] min-w-0 bg-muted/50 text-muted-foreground italic text-xs leading-snug break-words",
                        isMobile
                          ? "rounded-2xl px-3 py-1"
                          : "rounded-md px-2 py-0.5"
                      )}
                    >
                      This message was deleted
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={m.message_id}
                  className={cn(
                    "flex w-full min-w-0 items-start",
                    isMobile ? "gap-2" : "gap-3",
                    mine ? "justify-end" : "justify-start",
                    isMobile && cluster.startOfRun && "mt-2"
                  )}
                >
                  {showIncomingAvatar ? (
                    <SenderProfileHover
                      sender={m.sender}
                      sameOrg={sameOrg}
                      side="right"
                    >
                      <OrgMemberAvatar
                        user={m.sender}
                        className={cn(
                          "shrink-0 mt-0.5",
                          isMobile ? "h-7 w-7" : "h-9 w-9"
                        )}
                        fallbackClassName="text-xs font-semibold"
                      />
                    </SenderProfileHover>
                  ) : reserveAvatarSlot ? (
                    <div className="w-7 shrink-0" aria-hidden />
                  ) : null}
                  <DmMessageContextTarget
                    enabled={!isEditing}
                    className={cn(
                      "max-w-[min(85%,100%)] min-w-0 flex flex-col relative group overflow-visible md:pt-8",
                      mine ? "items-end" : "items-start"
                    )}
                    onOpen={({ x, y }) =>
                      setMessageMenu({ msg: m, x, y, mine })
                    }
                  >
                    {!isEditing ? (
                      <div
                        className={cn(
                          "absolute z-10 top-0 opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex items-center gap-0.5",
                          mine ? "left-0" : "right-0"
                        )}
                      >
                        {!readOnly ? (
                          <button
                            type="button"
                            title="Reply"
                            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-muted"
                            onClick={() => setReplyTo(m)}
                          >
                            <Reply className="h-3.5 w-3.5 pointer-events-none" />
                          </button>
                        ) : null}
                        {!readOnly ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                title="React"
                                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-muted"
                              >
                                <SmilePlus className="h-3.5 w-3.5 pointer-events-none" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="start"
                              className="flex w-auto min-w-0 flex-row gap-0.5 p-1.5"
                            >
                              {DM_QUICK_REACT_EMOJIS.map((emoji) => (
                                <DropdownMenuItem
                                  key={emoji}
                                  className="h-8 w-8 cursor-pointer justify-center p-0 text-base"
                                  onSelect={() => {
                                    void reactToMessage(m.message_id, emoji);
                                  }}
                                >
                                  {emoji}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : null}
                        {mine && !readOnly ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                aria-label="Message actions"
                                className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-border bg-background text-foreground shadow-sm hover:bg-muted"
                              >
                                <MoreHorizontal className="h-3.5 w-3.5 pointer-events-none" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start">
                              <DropdownMenuItem onClick={() => startEdit(m)}>
                                <Pencil className="h-3.5 w-3.5 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => handleDelete(m.message_id)}
                              >
                                <Trash2 className="h-3.5 w-3.5 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : null}
                      </div>
                    ) : null}
                    {(showSenderMeta || showTimestamp) && !isEditing ? (
                      <div
                        className={cn(
                          "flex items-baseline gap-2 mb-0.5 px-0.5 max-w-full min-w-0",
                          mine ? "flex-row-reverse" : ""
                        )}
                      >
                        {showSenderMeta && !mine ? (
                          <SenderProfileHover
                            sender={m.sender}
                            sameOrg={sameOrg}
                            side="bottom"
                            className="min-w-0 max-w-full"
                          >
                            <button
                              type="button"
                              className="text-sm font-semibold leading-tight text-foreground text-left hover:underline underline-offset-2 truncate max-w-full"
                            >
                              {personName(m.sender)}
                            </button>
                          </SenderProfileHover>
                        ) : null}
                        {showSenderMeta && !sameOrg ? (
                          <span className="text-[10px] font-medium rounded-full border border-border text-muted-foreground px-1.5 py-0.5 shrink-0 whitespace-nowrap">
                            {m.sender?.org_name || partnerName}
                          </span>
                        ) : null}
                        {showTimestamp ? (
                          <p className="text-[10px] text-muted-foreground leading-none shrink-0 whitespace-nowrap">
                            {m.edited_at ? "Edited · " : ""}
                            {formatDmTimestamp(m.created_at, {
                              compact: isMobile,
                            })}
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                    {m.reply_to ? (
                      <button
                        type="button"
                        className={cn(
                          "mb-1 max-w-full truncate rounded border-l-2 border-primary/50 bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground text-left",
                          mine && "self-end"
                        )}
                        onClick={() => setReplyTo(m)}
                        title="Reply again"
                      >
                        {m.reply_to.deleted
                          ? "Original message deleted"
                          : plainExcerpt(m.reply_to.content_preview, 80) ||
                            "Attachment"}
                      </button>
                    ) : null}
                    {isEditing ? (
                      <div className="w-full space-y-1.5">
                        <RichMessageEditor
                          ref={editComposerRef}
                          value={editDraft}
                          onChange={setEditDraft}
                          onSubmit={saveEdit}
                          minRows={2}
                          className="w-full"
                        />
                        <div className="flex gap-1.5 justify-end">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7"
                            onClick={cancelEdit}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            className="h-7"
                            onClick={saveEdit}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className={cn(
                          "leading-snug w-fit max-w-full min-w-0 break-words overflow-x-hidden",
                          isMobile
                            ? cn(
                                "rounded-2xl px-3 py-2",
                                mine
                                  ? "rounded-br-md bg-primary/20 text-foreground"
                                  : "rounded-bl-md bg-muted text-foreground"
                              )
                            : cn(
                                "rounded-md px-2.5 py-1.5",
                                mine
                                  ? "bg-primary/20 text-foreground border border-primary/35"
                                  : "bg-muted text-foreground"
                              )
                        )}
                      >
                        <MarkdownBody content={m.content} compact />
                        {partnerGifItemsFromMedia(m.media).map((item, i) => (
                          <DmGifAttachment
                            key={`gif-${m.message_id}-${i}`}
                            attachment={item}
                            className={
                              isMobile ? "max-h-48 rounded-xl" : undefined
                            }
                          />
                        ))}
                        {(() => {
                          const blob = partnerBlobAttachmentFromMedia(m.media);
                          return blob ? (
                            <DmFileAttachments
                              attachment={blob}
                              orgId={undefined}
                            />
                          ) : null;
                        })()}
                      </div>
                    )}
                    {!isEditing && !readOnly ? (
                      <DmMessageReactions
                        reactions={m.reactions}
                        align={mine ? "end" : "start"}
                        onReact={(emoji) =>
                          reactToMessage(m.message_id, emoji)
                        }
                      />
                    ) : !isEditing && (m.reactions || []).length ? (
                      <DmMessageReactions
                        reactions={m.reactions}
                        align={mine ? "end" : "start"}
                        onReact={() => {}}
                      />
                    ) : null}
                    {mine &&
                    !isEditing &&
                    seenReceiptMessageId === m.message_id ? (
                      <p className="text-[10px] text-muted-foreground mt-0.5 px-0.5">
                        Seen
                      </p>
                    ) : null}
                  </DmMessageContextTarget>
                </div>
              );
            })
          )}
          </div>
        </div>
      </div>

      {error ? (
        <p className="shrink-0 px-3 py-1.5 text-xs text-destructive">{error}</p>
      ) : null}

      {readOnly ? (
        <div className="border-t border-border p-3 shrink-0 bg-muted/30 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] md:pb-3">
          <p className="text-sm text-muted-foreground text-center">
            {legalHold
              ? "Messaging is frozen while legal hold is active."
              : channel?.write_block_reason ||
                "This partner channel is read-only."}
          </p>
        </div>
      ) : !channel ? (
        <div className="border-t border-border p-3 shrink-0 bg-muted/30 pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] md:pb-3">
          <p className="text-sm text-muted-foreground text-center">
            Loading composer…
          </p>
        </div>
      ) : (
        <form
          onSubmit={handleSend}
          className="border-t border-border px-2 pt-2 shrink-0 space-y-2 min-w-0 overflow-x-hidden pb-[max(0.5rem,env(safe-area-inset-bottom,0px))] md:p-2"
        >
          {replyTo ? (
            <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5 text-xs">
              <Reply className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate flex-1">
                Replying to:{" "}
                {plainExcerpt(replyTo.content, 80) || "Attachment"}
              </span>
              <button
                type="button"
                className="p-0.5 hover:opacity-70"
                onClick={() => setReplyTo(null)}
                aria-label="Cancel reply"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : null}
          {pendingGif ? (
            <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5 text-xs">
              <img
                src={pendingGif.preview_url || pendingGif.url}
                alt=""
                className="h-24 w-24 rounded object-cover shrink-0"
              />
              <span className="truncate flex-1">
                {pendingGif.title || "GIF"}
              </span>
              <button
                type="button"
                className="p-0.5 hover:opacity-70"
                onClick={() => setPendingGif(null)}
                aria-label="Remove attachment"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : null}
          {pendingFileCount > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {pendingLocalFiles.map((file, idx) => (
                <PendingLocalChip
                  key={`local-${file.name}-${file.size}-${idx}`}
                  file={file}
                  onRemove={() =>
                    setPendingLocalFiles((prev) =>
                      prev.filter((_, i) => i !== idx)
                    )
                  }
                />
              ))}
            </div>
          ) : null}
          {isMobile ? (
            <MobileMessageComposer
              composerRef={composerRef}
              value={draft}
              onChange={(v) => {
                setDraft(v);
                const now = Date.now();
                if (now - lastTypedEmitRef.current > 1200) {
                  lastTypedEmitRef.current = now;
                  emitTyping(channelId, true);
                }
              }}
              onSubmit={handleSend}
              onPaste={onComposerPaste}
              disabled={sending}
              sendDisabled={sending}
              placeholder={`Message ${partnerName}…`}
              orgId={orgId}
              onAttach={() => fileInputRef.current?.click()}
              attachDisabled={
                sending || Boolean(pendingGif) || remainingAttach <= 0
              }
              onGifSelect={(gif) => {
                setPendingLocalFiles([]);
                setPendingGif(gif);
              }}
              gifDisabled={sending || pendingFileCount > 0}
              onEmojiInsert={(native) =>
                composerRef.current?.insertText(native)
              }
            />
          ) : (
            <div className="flex items-end gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => fileInputRef.current?.click()}
                title="Attach files"
                disabled={sending || Boolean(pendingGif) || remainingAttach <= 0}
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              <GifPickerButton
                orgId={orgId}
                disabled={sending || pendingFileCount > 0}
                onSelect={(gif) => {
                  setPendingLocalFiles([]);
                  setPendingGif(gif);
                }}
              />
              <EmojiInsertButton
                disabled={sending}
                onInsert={(native) => composerRef.current?.insertText(native)}
              />
              <div onPaste={onComposerPaste} className="flex-1 min-w-0">
                <RichMessageEditor
                  ref={composerRef}
                  value={draft}
                  onChange={(v) => {
                    setDraft(v);
                    const now = Date.now();
                    if (now - lastTypedEmitRef.current > 1200) {
                      lastTypedEmitRef.current = now;
                      emitTyping(channelId, true);
                    }
                  }}
                  onSubmit={handleSend}
                  disabled={sending}
                  placeholder={`Message ${partnerName} and your team…`}
                  minRows={3}
                  className="flex-1"
                />
              </div>
              <Button
                type="button"
                size="icon"
                className="h-8 w-8 shrink-0"
                disabled={sending}
                title="Send (Enter)"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleSend}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept={TEAM_FILE_ACCEPT}
            multiple
            onChange={onPickFiles}
          />
          {typingUserId ? (
            <p className="text-[11px] text-muted-foreground px-1">
              Someone is typing…
            </p>
          ) : null}
          <p className="hidden md:block text-[11px] text-muted-foreground px-1">
            Visible to everyone in this channel from both organizations. File
            storage counts against both orgs (up to {MEDIA_MESSAGE_MAX_FILES}{" "}
            files per message).
          </p>
        </form>
      )}

      <PartnerMembersDialog
        open={membersOpen}
        onOpenChange={setMembersOpen}
        channel={channel}
        viewerOrgId={orgId}
        channelId={channelId}
        accessToken={accessToken}
        onChannelChange={applyChannelUpdate}
      />

      {channel?.can_manage_settings ? (
        <PartnerSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          channel={channel}
          orgId={orgId}
          channelId={channelId}
          accessToken={accessToken}
          onChannelChange={applyChannelUpdate}
        />
      ) : null}

      <DmMessageContextMenu
        open={Boolean(messageMenu?.msg)}
        x={messageMenu?.x ?? 0}
        y={messageMenu?.y ?? 0}
        onClose={closeMessageMenu}
        canReply={Boolean(messageMenu?.msg) && !readOnly}
        canReact={Boolean(messageMenu?.msg) && !readOnly}
        reactEmojis={DM_QUICK_REACT_EMOJIS}
        canCopy={Boolean(
          markdownToPlain(messageMenu?.msg?.content || "").trim()
        )}
        canEdit={Boolean(messageMenu?.mine) && !readOnly}
        canDelete={Boolean(messageMenu?.mine) && !readOnly}
        onReply={() => setReplyTo(messageMenu.msg)}
        onReact={(emoji) =>
          reactToMessage(messageMenu.msg.message_id, emoji)
        }
        onCopy={() => copyMessageText(messageMenu.msg)}
        onEdit={() => startEdit(messageMenu.msg)}
        onDelete={() => handleDelete(messageMenu.msg.message_id)}
      />
    </div>
  );
}
